import type { Language } from 'src/enums/language';
import { translateStaticTextIntoLanguage } from 'src/translators/static-text-translator';
import {
  BuildDomain,
  getPrimaryBuildTypeForBuildDomain,
  includeRejectButtonBuildDomains,
} from 'src/enums/build/build-domain';
import { colors, fonts } from 'src/styling/constants';
import type { StaticTextLookupKey } from 'src/translators/static-text-lookup';
import { preparePath } from 'src/helpers/navigation-helper';
import { getLanguageUrlPrefixForBuildType } from 'src/enums/build/build-type';

const COOKIE_POLICY_URL = '/policy/cookie-policy';
const COOKIE_POLICY_UPDATED = '22/01/2021';
const THIRD_PARTY_COOKIES_OPT_OUT_URL = '/policy/cookie-policy-management';

const ANALYTICAL_COOKIES_CONSENT_KEY = 'analytics';
const PREFERENCES_COOKIES_CONSENT_KEY = 'preferences';
const MARKETING_COOKIES_CONSENT_KEY = 'marketing';
const SOCIAL_MEDIA_COOKIES_CONSENT_KEY = 'social_media';

const COOKIE_CONSENT_CHANGED_EVENT_KEY = 'cookieConsentChanged';

let openConsentBanner: (() => void) | undefined = undefined;

export const initialiseCookieConsentBanner = (lang: Language, buildDomain: BuildDomain): void => {
  const config = getConfig(lang, buildDomain);

  if (!window.CookieControl) {
    // eslint-disable-next-line no-console
    console.warn('CookieControl is missing');
    return;
  }

  window.CookieControl.load(config);
  openConsentBanner = window.CookieControl.open;
};

export const openCookieConsentBanner = (): void => {
  // There's inconsistency between browsers around when this function becomes available
  const open = openConsentBanner ?? window.CookieControl.open;

  if (open === undefined) {
    // eslint-disable-next-line no-console
    console.warn('Cookie banner cannot be opened: not initialised yet.');
    return;
  }

  open();
};

const getConfig = (lang: Language, buildDomain: BuildDomain) => {
  const isMeridian = buildDomain === BuildDomain.MERIDIAN;

  return {
    apiKey: isMeridian
      ? '4da3aba3b97ec9fe4ddc80ba51035ff195768a6e'
      : '9b91d04b99e26753df1505f1bc6d66f2ed074f58',
    product: isMeridian ? 'PRO' : 'PRO_MULTISITE',
    initialState: 'notify',
    rejectButton: shouldIncludeRejectButton(buildDomain),
    notifyDismissButton: false,
    settingsStyle: 'button',
    closeOnGlobalChange: true,
    position: 'left',
    theme: 'light',
    closeStyle: 'button',
    mode: 'GDPR',

    text: {
      title: translateStaticTextIntoLanguage('cookieConsentBannerTitle', lang),
      intro: translateStaticTextIntoLanguage('cookieConsentBannerIntro', lang),
      accept: translateStaticTextIntoLanguage('cookieConsentBannerAccept', lang),
      acceptSettings: translateStaticTextIntoLanguage('cookieConsentBannerAcceptSettings', lang),
      reject: translateStaticTextIntoLanguage('cookieConsentBannerReject', lang),
      rejectSettings: translateStaticTextIntoLanguage('cookieConsentBannerRejectSettings', lang),
      settings: translateStaticTextIntoLanguage('cookieConsentBannerSettings', lang),
      necessaryTitle: translateStaticTextIntoLanguage('cookieConsentBannerNecessaryTitle', lang),
      necessaryDescription: translateStaticTextIntoLanguage(
        'cookieConsentBannerNecessaryDescription',
        lang
      ),
      on: translateStaticTextIntoLanguage('cookieConsentBannerOn', lang),
      off: translateStaticTextIntoLanguage('cookieConsentBannerOff', lang),
      thirdPartyTitle: translateStaticTextIntoLanguage('cookieConsentBannerThirdPartyTitle', lang),
      thirdPartyDescription: translateStaticTextIntoLanguage(
        'cookieConsentBannerThirdPartyDescription',
        lang
      ),
      closeLabel: translateStaticTextIntoLanguage('cookieConsentBannerCloseLabel', lang),
      notifyTitle: translateStaticTextIntoLanguage('cookieConsentBannerNotifyTitle', lang),
      notifyDescription: translateStaticTextIntoLanguage(
        'cookieConsentBannerNotifyDescription',
        lang
      ),
    },
    statement: {
      description: translateStaticTextIntoLanguage('cookieConsentBannerStatementDescription', lang),
      name: translateStaticTextIntoLanguage('cookieConsentBannerStatementName', lang),
      url: getCookiePolicyUrl(buildDomain, lang),
      updated: COOKIE_POLICY_UPDATED,
    },
    branding: getBranding(buildDomain),

    necessaryCookies: getNecessaryCookies(),

    optionalCookies: [
      {
        name: ANALYTICAL_COOKIES_CONSENT_KEY,
        label: translateStaticText('cookieConsentBannerOptionalCookiesAnalyticsLabel', lang),
        description: translateStaticText(
          'cookieConsentBannerOptionalCookiesAnalyticsDescription',
          lang
        ),
        cookies: getAnalyticsCookies(),
        thirdPartyCookies: getThirdPartyCookies(),
        onAccept: () => handleAnalyticsCookieChange({ analytics_accepted: true }),
        onRevoke: () => handleAnalyticsCookieChange({ analytics_accepted: false }),
      },
      {
        name: PREFERENCES_COOKIES_CONSENT_KEY,
        label: translateStaticText('cookieConsentBannerOptionalCookiesPreferencesLabel', lang),

        description: translateStaticText(
          'cookieConsentBannerOptionalCookiesPreferencesDescription',
          lang
        ),

        cookies: getPreferencesCookies(),
        thirdPartyCookies: getThirdPartyCookies(),
        onAccept: () => handleConsentCookieChange({ preferences_accepted: true }),
        onRevoke: () => handleConsentCookieChange({ preferences_accepted: false }),
      },
      {
        name: MARKETING_COOKIES_CONSENT_KEY,
        label: translateStaticText('cookieConsentBannerOptionalCookiesMarketingLabel', lang),

        description: translateStaticText(
          'cookieConsentBannerOptionalCookiesMarketingDescription',
          lang
        ),
        cookies: getMarketingCookies(),
        thirdPartyCookies: getThirdPartyCookies(),
        onAccept: () => handleConsentCookieChange({ marketing_accepted: true }),
        onRevoke: () => handleConsentCookieChange({ marketing_accepted: false }),
      },
      {
        name: SOCIAL_MEDIA_COOKIES_CONSENT_KEY,
        label: translateStaticText('cookieConsentBannerOptionalCookiesSocialMediaLabel', lang),

        description: translateStaticText(
          'cookieConsentBannerOptionalCookiesSocialMediaDescription',
          lang
        ),

        cookies: getSocialMediaCookies(),
        thirdPartyCookies: getThirdPartyCookies(),
        onAccept: () => handleConsentCookieChange({ social_media_accepted: true }),
        onRevoke: () => handleConsentCookieChange({ social_media_accepted: false }),
      },
    ],
  };
};

const translateStaticText = (translateKey: StaticTextLookupKey, lang: Language) =>
  JSON.stringify(translateStaticTextIntoLanguage(translateKey, lang));

const shouldIncludeRejectButton = (buildDomain: BuildDomain): boolean => {
  return includeRejectButtonBuildDomains.includes(buildDomain);
};

const getCookiePolicyUrl = (buildDomain: BuildDomain, language: Language) => {
  // Note: cookie policy pages don't use a translated /policy/ slug

  const languagePrefix = getLanguageUrlPrefixForBuildType(
    language,
    getPrimaryBuildTypeForBuildDomain(buildDomain)
  );
  const path = `/${languagePrefix}/${COOKIE_POLICY_URL}/`;

  return preparePath(path);
};

const getBranding = (buildDomain: BuildDomain) => {
  const commonBrandingSettings = {
    fontSizeTitle: '1.4em',
    fontSizeHeaders: '1.2em',
    removeAbout: true,
    removeIcon: true,
  };

  switch (buildDomain) {
    case BuildDomain.HARBOUR:
      const harbourFontColor = colors.harbourSlateGrey;
      const harbourGold = colors.harbourGold;
      return {
        ...commonBrandingSettings,
        fontColor: harbourFontColor,
        fontFamily: fonts.harbour.fontFamily,
        fontSize: '1em',
        notifyFontColor: harbourFontColor,
        backgroundColor: colors.davidLloydGray,
        toggleText: harbourGold,
        toggleColor: harbourGold,
        toggleBackground: colors.lightGray,
        alertText: harbourFontColor,
        alertBackground: colors.greyD,
        acceptText: colors.defaultPromoTextColor,
        acceptBackground: harbourGold,
      };
    case BuildDomain.MERIDIAN:
      const meridianFontColor = colors.meridianBody;
      const meridianHighlight = colors.meridianHighlight;
      return {
        ...commonBrandingSettings,
        fontColor: meridianFontColor,
        fontFamily: fonts.meridian.fontFamily,
        fontSize: '1em',
        notifyFontColor: meridianFontColor,
        backgroundColor: colors.davidLloydGray,
        toggleText: meridianHighlight,
        toggleColor: meridianHighlight,
        toggleBackground: colors.lightGray,
        alertText: meridianFontColor,
        alertBackground: colors.greyD,
        acceptText: colors.defaultPromoTextColor,
        acceptBackground: meridianHighlight,
      };
    default:
      const davidLloydFontColor = colors.davidLloydBrown;
      const camelot = colors.camelot;
      return {
        ...commonBrandingSettings,
        fontColor: davidLloydFontColor,
        fontFamily: fonts.regular.fontFamily,
        fontSize: '0.9rem',
        notifyFontColor: davidLloydFontColor,
        backgroundColor: colors.lightGray,
        toggleText: davidLloydFontColor,
        toggleColor: camelot,
        toggleBackground: colors.greyD,
        alertText: davidLloydFontColor,
        alertBackground: colors.greyD,
        acceptText: colors.defaultPromoTextColor,
        acceptBackground: camelot,
      };
  }
};

const getNecessaryCookies = () => [
  '__RequestVerificationToken',
  'ASP.NET_SessionId',
  '__cfduid',
  'JSESSIONID',
  'X-Mapping-#',
  '.ASPXAUTH',
  'SC_ANALYTICS_GLOBAL_COOKIE',
  '__RequestVerificationToken',
  '_grecaptcha',
  '_GRECAPTCHA',
  'test_cookie',
];

const getAnalyticsCookies = () => [
  'HasEnquired',
  'IsMember',
  'david-lloyd_new-club-page-experience',
  'FormTracking',
  '__qca',
  '_dc_gtm_UA-#',
  '_ga',
  '_gat',
  '_gid',
  'optimizely_data$$pending_events',
  'optimizelyEndUserId',
  '_hjid',
  '_hjTLDTest',
  'bx_bucket_number',
  '_hjAbsoluteSessionInProgress',
  '_hjIncludedInSessionSample',
  '_hjIncludedInPageViewSample',
  'AnalyticsSyncHistory',
  'oo',
  'https://tracking.monsido.com/',
  '_ga_Q17WCLZRSB',
  '_ga_886PT6X2T7',
  '_ga_5HXT2C1V3E',
  '_ga_XBEGQYYEGB',
  '_ga_0MS8JYT181',
  '_ga_Y05DCCJE8P',
  '_ga_WP8VL08V82',
  '_gcl_au',
  '_ga_L277XMBJKG',
  '_ga_VSFNPTKSK0',
  '_ga_B671ER93TS',
];

const getPreferencesCookies = () => [
  'davidlloydbe#lang',
  'davidlloydde#lang',
  'davidlloydes#lang',
  'davidlloydfr#lang',
  'davidlloydie#lang',
  'davidlloydit#lang',
  'davidlloydnl#lang',
  'davidlloyduk#lang',
  '@@scroll#',
];

const getMarketingCookies = () => [
  '_fbp',
  '_gcl_au',
  'optimizely_data$$#$$event_queue',
  'optimizely_data$$#$$events',
  'optimizely_data$$#$$layer_map',
  'optimizely_data$$#$$layer_states',
  'optimizely_data$$#$$sesson_state',
  'optimizely_data$$#$$tracker_optimizely',
  'optimizely_data$$#$$variation_map',
  'optimizely_data$$#$$visitor_profile',
  'optimizelyDomainTestCookie',
  '_pin_unauth',
  '_hjIncludedInSample',
  '_hjRecordingEnabled',
  'adm.sesh',
  'adm.usmeta',
  'bid_dlhvjhttfcspzofprwbdxbat2qc2ryvq',
  'bStorage',
  'tag',
  '_psegs',
  'ck',
  'CMRUM3',
  'mako_uid',
  'match',
  'match/bounce',
  'mc',
  'oo',
  'pcs/activeview',
  'permutive-id',
  'permutive-session',
  'pt',
  'tr',
  'track/pxl',
];

const getSocialMediaCookies = (): Array<unknown> => [];

const getThirdPartyCookies = () => [
  {
    name: '',
    optOutLink: THIRD_PARTY_COOKIES_OPT_OUT_URL,
  },
];

type OnAnalyticsCookieConsentChangeArgs = { analytics_accepted: boolean };
type OnCookieConsentChangeArgs =
  | OnAnalyticsCookieConsentChangeArgs
  | { preferences_accepted: boolean }
  | { marketing_accepted: boolean }
  | { social_media_accepted: boolean };

const handleConsentCookieChange = (consentChange: OnCookieConsentChangeArgs): void => {
  if (!window) {
    return;
  }
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push(Object.assign({ event: COOKIE_CONSENT_CHANGED_EVENT_KEY }, consentChange));
};

const handleAnalyticsCookieChange = (consentChange: OnAnalyticsCookieConsentChangeArgs): void => {
  allowAmplitudeEvents(consentChange.analytics_accepted);
  handleConsentCookieChange(consentChange);
};

const allowAmplitudeEvents = (allow: boolean): void => {
  if (!window) {
    return;
  }
  window.launchAmplitude = window.launchAmplitude || function () {};

  window.launchAmplitude({ allow });
};
